import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from '@mui/x-data-grid';
import { ProductModal } from "./ProductModal";
import { ItemModal } from "./ItemModal";
import { Loader, DataGridWrapper } from "../GeneralComponents";
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';

function Admin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inquiries');
  const [loadingData, setLoadingData] = useState(true);
  const [loadingInquiries, setLoadingInquiries] = useState(true);
  const [productData, setProductData] = useState({});
  const [itemData, setItemData] = useState({});
  const [inquiryTableData, setInquiryTableData] = useState([]);
  const [productModalOpened, setProductModalOpened] = useState(false);
  const [itemModalOpened, setItemModalOpened] = useState(false);
  const [productTableData, setProductTableData] = useState([]);
  const [itemTableData, setItemTableData] = useState([]);

  const [editProductId, setEditProductId] = useState("");
  const [editItemId, setEditItemId] = useState("");

  const LOCAL_URL = ['http://localhost:3001', 'http://127.0.0.1:3001', 'http://192.168.50.185:3001'];
  const LOCAL_TEST = LOCAL_URL.includes(window.location.origin);
  const currentSessionToken = getCookie('__session');

  useEffect(() => {
    if (!currentSessionToken) {
      navigate('/');
      return;
    }
    getProductData();
    getInquiryData();
  }, []);

  useEffect(() => {
    // Process Response into Data Grid Objects
    const tempTableData = [];
    const dataRows = Object.keys(productData);
    if (dataRows.length > 0){
      dataRows.forEach((productId) => {
        const product = productData[productId];
        const row = {
          id: product.id,
          nameEN: product.name.en,
          nameCH: product.name.ch,
          tags: product.tags,
          descriptionEN: product.description.en,
          descriptionCH: product.description.ch,
        }
        tempTableData.push(row);
      })
    }
    setProductTableData(tempTableData);
  }, [productData]);

  useEffect(() => {
    // Process Response into Data Grid Objects
    const tempTableData = [];
    const dataRows = Object.keys(itemData);
    if (dataRows.length > 0){
      dataRows.forEach((itemId) => {
        const item = itemData[itemId];
        // const categoryString = item.productCategory.reduce(
        //   (accumulator, currentValue) => { return accumulator + `${productData[currentValue].name.en}, `},
        //   "",
        // );
        const categoryStrings = [];
        item.productCategory.forEach((productId) => {
          if (productData[productId] && productData[productId].name?.en) {
            categoryStrings.push(productData[productId].name?.en);
          }
        })
        const row = {
          id: item.id,
          nameEN: item.name.en,
          nameCH: item.name.ch,
          price: item.priceInCents/100,
          descriptionEN: item.description.en,
          descriptionCH: item.description.ch,
          category: categoryStrings.join(", ")
        }
        tempTableData.push(row);
      })
    }
    setItemTableData(tempTableData);
  }, [itemData]);

  function getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  function getBaseUrl() {
    let baseUrl = "";
    if (LOCAL_TEST) {
      let currentUrl = window.location.origin.slice(0, -5);
      baseUrl = `${currentUrl}:5000`;
    }
    return baseUrl;
  }

  async function getProductData() {
    setLoadingData(true);
    const baseUrl = getBaseUrl();

    const getProductsRequest = fetch(`${baseUrl}/api/getAllProducts`, {
      method: 'GET',
    });
    const getItemsRequest = fetch(`${baseUrl}/api/getAllItems`, {
      method: 'GET',
    });
    const [getProductsResponse, getItemsResponse] = await Promise.all([getProductsRequest, getItemsRequest]);
    const productDocs = await getProductsResponse.json();
    const itemDocs = await getItemsResponse.json();

    if (productDocs.auth === false || itemDocs.auth === false) {
      navigate('/');
      return;
    }

    setProductData(productDocs.data || {});
    setItemData(itemDocs.data || {});
    setLoadingData(false);
  }

  async function getInquiryData() {
    setLoadingInquiries(true);
    const baseUrl = getBaseUrl();

    const response = await fetch(`${baseUrl}/api/getAllInquiries`, {
      method: 'GET',
    });
    const result = await response.json();

    if (result.auth === false) {
      navigate('/');
      return;
    }

    setInquiryTableData(result.data || []);
    setLoadingInquiries(false);
  }

  function handleLogout() {
    window.location.href = `${getBaseUrl()}/logout`;
  }

  function handleProductModalOpen(state) {
    setProductModalOpened(state);
  }

  function handleItemModalOpen(state) {
    setItemModalOpened(state);
  }

  function handleProductEditToggle(productId){
    setEditProductId(productId);
  }

  function handleItemEditToggle(itemId){
    setEditItemId(itemId);
  }

  function renderTags(props) {
    const { hasFocus, value } = props;
    let tags = '';
    if (value && value.length > 0) {
      for (let i = 0; i < value.length; i++){
        tags += value[i];
        if (i != value.length - 1){
          tags += ', '
        };
      }
    }
    return tags
  }

  function renderProductEditBtn(props) {
    const { hasFocus, value } = props;
    const editButton = (
      <button className="btn btn-primary btn-sm" onClick={()=>{
        handleProductEditToggle(value);
      }}>Edit</button>
    )
    return editButton
  }

  function renderItemEditBtn(props) {
    const { hasFocus, value } = props;
    const editButton = (
      <button className="btn btn-primary btn-sm" onClick={()=>{
        handleItemEditToggle(value);
      }}>Edit</button>
    )
    return editButton
  }

  function renderItemCategory(props) {
    const { hasFocus, value } = props;
    const editButton = (
      <button className="btn btn-primary btn-sm" onClick={()=>{
        handleItemEditToggle(value);
      }}>Edit</button>
    )
    return editButton
  }

  const productColumns = [
    { field: 'nameEN', headerName: 'Name (EN)', width: 100, flex: 1, },
    { field: 'nameCH', headerName: 'Name (CH)', width: 100, flex: 1, },
    { field: 'tags', headerName: 'Tags', width: 100, flex: 0, renderCell: renderTags},
    { field: 'descriptionEN', headerName: 'description (EN)', width: 130, flex: 1, },
    { field: 'descriptionCH', headerName: 'description (CH)', width: 130, flex: 1, },
    { field: 'id', headerName: 'edit', width: 100, flex: 0, renderCell: renderProductEditBtn },
  ];

  const itemColumns = [
    { field: 'nameEN', headerName: 'Name (EN)', width: 100, flex: 1, },
    { field: 'nameCH', headerName: 'Name (CH)', width: 100, flex: 1, },
    { field: 'price', headerName: 'Price $', width: 130, flex: 1, },
    { field: 'category', headerName: 'Item Category', width: 120, flex: 1},
    { field: 'descriptionEN', headerName: 'description (EN)', width: 130, flex: 1, },
    { field: 'descriptionCH', headerName: 'description (CH)', width: 130, flex: 1, },
    { field: 'id', headerName: 'edit', width: 100, flex: 0, renderCell: renderItemEditBtn },
  ];

  const inquiryColumns = [
    { field: 'submittedAt', headerName: 'Submitted', width: 150, flex: 0.8 },
    { field: 'name', headerName: 'Name', width: 130, flex: 0.8 },
    { field: 'email', headerName: 'Email', width: 170, flex: 1 },
    { field: 'phone', headerName: 'Phone', width: 130, flex: 0.7 },
    { field: 'weddingDate', headerName: 'Wedding Date', width: 120, flex: 0.7 },
    { field: 'venue', headerName: 'Venue', width: 140, flex: 1 },
    { field: 'guestCount', headerName: 'Guests', width: 90, flex: 0.5 },
    { field: 'floralBudget', headerName: 'Budget', width: 140, flex: 0.8 },
    { field: 'servicesNeeded', headerName: 'Services Needed', width: 160, flex: 1 },
    { field: 'message', headerName: 'Message', width: 240, flex: 1.5 },
  ];

  return (
    <Box sx={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f4f5f7' }}>
      <ProductModal
        modalOpened={productModalOpened}
        productData={productData}
        editProductId={editProductId}
        handleModalOpen={handleProductModalOpen}
        setTargetId={setEditProductId}
        refreshTableData={getProductData}
      />
      <ItemModal
        modalOpened={itemModalOpened}
        editItemId={editItemId}
        handleModalOpen={handleItemModalOpen}
        refreshTableData={getProductData}
        setTargetId={setEditItemId}
        productData={productData}
        itemData={itemData}
      />
      <AppBar position="static" color="inherit" elevation={1} sx={{ bgcolor: '#fff' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Lynsis Admin
          </Typography>
          <Button color="error" variant="outlined" startIcon={<LogoutIcon />} onClick={handleLogout}>
            Log Out
          </Button>
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => { setActiveTab(newValue); }}
          sx={{ px: 2 }}
        >
          <Tab label="Inquiries" value="inquiries" />
          <Tab label="Product Catalog" value="catalog" />
        </Tabs>
      </AppBar>
      <Box component='div' sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', py: 3 }}>
        <Container maxWidth="xl">
          {activeTab === 'inquiries' ? (
            loadingInquiries ?
              <Loader /> :
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Wedding Inquiries</Typography>
                <Box sx={{ height: 640, width: '100%' }}>
                  <DataGridWrapper tableData={inquiryTableData} columns={inquiryColumns} />
                </Box>
              </Paper>
          ) : (
            loadingData ?
              <Loader /> :
              <Stack spacing={3}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2}>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => { handleProductModalOpen(true); }}>
                      Add Product Category
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => { handleItemModalOpen(true); }}>
                      Add Item
                    </Button>
                  </Stack>
                </Paper>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Products</Typography>
                  <Box sx={{ height: 420, width: '100%' }}>
                    <DataGridWrapper tableData={productTableData} columns={productColumns} />
                  </Box>
                </Paper>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Items</Typography>
                  <Box sx={{ height: 420, width: '100%' }}>
                    <DataGridWrapper tableData={itemTableData} columns={itemColumns} />
                  </Box>
                </Paper>
              </Stack>
          )}
        </Container>
      </Box>
    </Box>
  );
}

export { Admin };
